export function getDesignIssue(editor) {
  if (!editor || typeof editor.state !== 'function' || !Array.isArray(editor.slots)) return { code: 'initializing' };

  const state = editor.state();
  if (!state || !state.memberCount || !Array.isArray(state.characters)) return { code: 'count' };

  for (let index = 0; index < state.memberCount; index += 1) {
    const slot = editor.slots[index];
    if (slot?.error) return { code: 'error', slot: index + 1 };
    if (!slot?.ready) return { code: 'loading', slot: index + 1 };
  }

  return null;
}

export function isDesignReady(editor) {
  return getDesignIssue(editor) === null;
}

export function issueLabel(issue, language = 'nl') {
  const lang = language === 'de' ? 'de' : 'nl';
  const slot = issue?.slot || 1;
  const labels = {
    nl: {
      initializing: 'Even geduld, je ontwerp wordt geladen…',
      count: 'Kies het aantal personen',
      loading: `Even geduld, persoon ${slot} wordt geladen…`,
      error: `Controleer persoon ${slot}`,
    },
    de: {
      initializing: 'Einen Moment, dein Design wird geladen…',
      count: 'Anzahl Personen wählen',
      loading: `Einen Moment, Person ${slot} wird geladen…`,
      error: `Person ${slot} überprüfen`,
    },
  };
  return labels[lang][issue?.code] || (lang === 'de' ? 'Design überprüfen' : 'Controleer je ontwerp');
}

function languageForHost(host) {
  const hostname = String(host?.dataset?.storefrontHost || window.location.hostname || '').toLowerCase();
  if (hostname === 'cozytree.de' || hostname.endsWith('.cozytree.de')) return 'de';
  return 'nl';
}

export function revealDesignIssue(host, issue) {
  const root = host?.shadowRoot;
  if (!root || !issue) return false;

  let target = null;
  let focusTarget = null;

  if (issue.code === 'count') {
    target = root.querySelector('#counts');
    focusTarget = target?.querySelector('button');
  } else if (issue.slot) {
    host.editor?.setActivePerson?.(issue.slot, false);
    target = root.querySelector(`#ct-person-${issue.slot}`);
  }

  if (!target) return false;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  window.setTimeout(() => focusTarget?.focus?.({ preventScroll: true }), 350);
  return true;
}

// Manifest products own their Q1/session/ATC lifecycle in Design Runtime V2.
const hasManifest = host => !!(host?.dataset.packageDesign || host?.dataset.design);
function mountProductAtcGate(root = document) {
  const host = root.querySelector?.('cozytree-personalizer');
  if (!host || hasManifest(host) || host.dataset.cozyAtcGateMounted === 'true') return;

  const product = host.closest('.product');
  const button = product?.querySelector('[data-cozy-product-atc]');
  if (!button) return;

  host.dataset.cozyAtcGateMounted = 'true';

  const label = button.querySelector(':scope > span');
  const baseDisabled = button.dataset.cozyBaseDisabled === 'true';
  const incompleteLabel = button.dataset.cozyIncompleteLabel || 'Controleer je ontwerp';
  const readyLabel = button.dataset.cozyReadyLabel || 'Design opslaan';
  const language = languageForHost(host);

  const sync = () => {
    if (hasManifest(host) || !host.isConnected || !button.isConnected || baseDisabled) return;

    const issue = getDesignIssue(host.editor);
    const ready = issue === null;
    const overrideLabel = button.dataset.cozyBundleLabel || '';
    const expectedDisabled = false;
    const expectedLabel = overrideLabel || (ready ? readyLabel : (issue ? issueLabel(issue, language) : incompleteLabel));

    if (button.disabled !== expectedDisabled) button.disabled = expectedDisabled;
    if (button.getAttribute('aria-disabled') !== String(expectedDisabled)) {
      button.setAttribute('aria-disabled', String(expectedDisabled));
    }
    if (button.dataset.cozyDesignReady !== String(ready)) {
      button.dataset.cozyDesignReady = String(ready);
    }
    if (label && label.textContent !== expectedLabel) label.textContent = expectedLabel;
  };

  const revealMissing = (event) => {
    if (hasManifest(host) || button.dataset.cozyBundleLabel) return;
    const issue = getDesignIssue(host.editor);
    if (!issue) return;
    event.preventDefault();
    event.stopPropagation();
    if (issue.code === 'initializing') return;
    revealDesignIssue(host, issue);
  };

  button.addEventListener('click', revealMissing);
  sync();
  const timer = window.setInterval(sync, 200);
  window.addEventListener('pagehide', () => window.clearInterval(timer), { once: true });
}

function boot() {
  mountProductAtcGate(document);
  document.addEventListener('shopify:section:load', (event) => mountProductAtcGate(event.target));
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
